<template>
  <div
    class="
      flex-grow-0
      md:flex-1
      rounded-2xl
      bg-white-005
      flex flex-col
      px-6
      py-4
      mb-6
      md:mb-0
    "
    :style="`height:${isMediumScreen ? '428px' : '343px'}`"
  >
    <div class="flex justify-between items-center mb-4">
      <div class="font-bold text-sm lg:text-xl">Distribution</div>
      <ShareBtn tip="Distribution"></ShareBtn>
    </div>

    <div class="flex-grow-0 md:flex-1 min-h-0 h-50 md:h-full">
      <Skeletor class="w-full h-full rounded" v-if="props.loading"></Skeletor>

      <div class="w-full h-full flex flex-row items-center" v-else>
        <div
          class="w-full h-50 md:h-full flex-grow-0 md:flex-1 min-h-0 min-w-0"
          ref="container"
        >
          <VChart
            class="w-48 md:w-full h-48 md:h-full"
            ref="chart"
            :option="option"
            :key="width"
          ></VChart>
        </div>
        <div
          class="
            md:space-x-0 md:space-y-6
            flex flex-col
            justify-center
            md:ml-14 md:mr-4
            text-xs
            md:text-base
          "
        >
          <div
            class="flex items-center cursor-pointer mt-2"
            @mouseenter="onHover(item)"
            @mouseleave="onUnhover(item)"
            v-for="(item, i) in legend"
            :key="i"
          >
            <div
              class="rounded w-2 h-2 md:w-4 md:h-3 mr-2 flex-shrink-0"
              :style="`background:${item.color}`"
            ></div>
            <div>
              {{ item.text }}
              <span
                style="color: #ffffffb3"
                v-if="!['FT', 'NFT'].includes(item.text)"
                >(NFT)</span
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { isMdScreen } from '@/hooks';
import { localeNumber } from '@/utils';
import { useElementSize } from '@vueuse/core';
import { PieChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { computed, defineProps, onMounted, ref } from 'vue';
import VChart from 'vue-echarts';
import { Skeletor } from 'vue-skeletor';
import ShareBtn from './ShareBtn.vue';

use([
  CanvasRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
]);

const chart = ref(null);

const container = ref(null);

const { width, height } = useElementSize(container);
const isMediumScreen = isMdScreen();
const props = defineProps({
  loading: {
    type: Boolean,
    default: true,
  },
  data: {
    type: Array,
    default: () => [],
  },
});

const nftColor = [
  '#3A0CA3FF',
  '#3F37C9FF',
  '#4361EEFF',
  '#4895EFFF',
  '#4CC9F0FF',
];

const legend = computed(() => {
  const labels = [
    {
      text: 'FT',
      color: '#F72585FF',
    },
    {
      text: 'NFT',
      color: '#3A0CA3FF',
    },
  ];
  props.data.forEach((x, i) => {
    if (x.assetType != 'FT') {
      labels.push({ text: x.assetType, color: nftColor[i] });
    }
  });

  return labels;
});

const project = computed(() => {
  const res = [];

  props.data.forEach((x) => {
    if (!['FT', 'NFT'].includes(x.assetType)) {
      res.push(x.assetType);
    }
  });

  return res;
});

const option = computed(() => {
  const legend = props.data.map((x) => x.assetType);

  const categoryData = [];
  const assetData = [];
  let i = 1;
  let nftValue = 0;
  props.data.forEach((x) => {
    if (x.assetType == 'FT') {
      assetData[0] = {
        value: x.totalValueUsd,
        name: x.assetType,
        itemStyle: {
          color: '#F72585FF',
        },
      };

      categoryData.push({
        value: x.totalValueUsd,
        name: 'FT',
        itemStyle: {
          color: '#F72585FF',
        },
      });
    } else {
      nftValue += x.totalValueUsd;
      assetData[i] = {
        value: x.totalValueUsd,
        name: x.assetType,
        itemStyle: {
          color: nftColor[i++],
        },
      };
    }
  });

  categoryData.push({
    name: 'NFT',
    value: nftValue,
    itemStyle: {
      color: '#3A0CA3FF',
    },
  });

  return {
    tooltip: {
      trigger: 'item',
      position: 'top',
      formatter: (params) => {
        const { data, percent } = params;
        return `${data.name}: $${localeNumber(data.value)} (${percent}%)`;
      },
    },
    legend: {
      right: 'center',
      data: legend,
      show: false,
    },
    grid: {
      left: '0',
      right: '0',
      bottom: '0',
      top: '0',
      containLabel: true,
    },
    series: [
      {
        name: 'Asset Distribution',
        type: 'pie',
        radius: [0, '40%'],
        // color: ['#F72585FF', '#3A0CA3FF'],
        label: {
          position: 'inner',
          fontSize: 14,
        },
        labelLine: {
          show: false,
        },
        data: categoryData,
        tooltip: {
          borderColor: '#FFFFFF66',
          backgroundColor: '#1e1f21bb',
          textStyle: {
            color: '#fff',
          },
        },
      },
      {
        name: 'Asset Distribution',
        type: 'pie',
        radius: ['50%', '90%'],
        // color: [
        //   '#F72585FF',
        //   '#3F37C9FF',
        //   '#4361EEFF',
        //   '#4895EFFF',
        //   '#4CC9F0FF',
        // ],
        label: {
          show: false,
        },

        labelLine: {
          show: false,
        },
        data: assetData,
        tooltip: {
          borderColor: '#FFFFFF66',
          backgroundColor: '#1e1f21bb',
          textStyle: {
            color: '#fff',
          },
        },
      },
    ],
  };
});

const getName = (item) => {
  let name = [];

  if (item.text == 'FT') {
    name = ['FT'];
  } else if (item.text == 'NFT') {
    name = ['NFT', ...project.value];
  } else {
    name = ['NFT', item.text];
  }
  return name;
};

const onHover = (item) => {
  const name = getName(item);

  if (item.text == 'FT') {
    chart.value.dispatchAction({
      type: 'showTip',
      seriesIndex: 0,
      dataIndex: 0,
      position: 'right',
    });
  }
  if (item.text == 'NFT') {
    chart.value.dispatchAction({
      type: 'showTip',
      seriesIndex: 0,
      dataIndex: 1,
      position: 'right',
    });
  }

  if (item.text !== 'NFT') {
    chart.value.dispatchAction({
      type: 'showTip',
      seriesIndex: 1,
      dataIndex: project.value.findIndex((x) => x == item.text) + 1,
      position: 'right',
    });
  }

  chart.value.dispatchAction({
    type: 'highlight',
    name,
  });
};

const onUnhover = (item) => {
  const name = getName(item);

  chart.value.dispatchAction({
    type: 'downplay',
    name,
  });
  chart.value.dispatchAction({
    type: 'hideTip',
  });
};

onMounted(() => {
  //   setTimeout(() => {
  //     dispaC;
  //   }, 3000);
});
</script>

<style lang="scss" scoped></style>
